import React, { useEffect } from "react";
import type { GetServerDataProps } from "gatsby";

import { MetaComponent } from "@bared/ui";
import { PageFlexibleSchema, filterProjection } from "@bared/groq";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { useTripleWhale } from "~/hooks/useTripleWhale";
import { useNostoAnalytics } from "~/hooks/useNostoAnalytics";

import { seoConfigForPage, getSeoMeta } from "~/lib/seo";
import { contextFromServerDataProps } from "~/lib/server";

import { ErrorPage } from "~/components/Error/Error";
import { Sections } from "~/components/Sections/Sections";

const pageQuery = filterProjection(
  PageFlexibleSchema,
  `_type == "pageFlexible" && slug.current == $slug && language == $language`,
);

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const { respond, notFound, groq, locale, sectionResolver } = context;

  // Resolve slug.
  const slug = props.params?.["*"];
  if ("string" !== typeof slug) throw new Error("No slug resolved.");

  // Fetch pageFlexible from sanity.
  const [pageDefault, pageLocalised] = await Promise.all([
    groq(pageQuery, {
      slug,
      locale: null,
      language: "default",
    }),
    groq(pageQuery, {
      slug,
      locale: locale.identifier,
      language: locale.identifier,
    }),
  ]);

  // Pick a page.
  const page = pageLocalised ?? pageDefault;

  // Page not found in Sanity, return 404.
  if (!page || !!page.softUnpublish) return notFound();

  // Resolve seo config.
  const seo = seoConfigForPage({ context, sanityMetadata: page.metadata });

  // Resolve sections.
  page.content = await sectionResolver.resolve({
    sanitySections: page.content,
  });

  const transparentHeader = sectionResolver.shouldHeaderBeTransparent(
    page.content,
  );

  // Respond with server data.
  return respond({ page, seo, transparentHeader } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export default function FlexiblePage({ serverData }: PageProps<ServerData>) {
  const tripleWhale = useTripleWhale();
  const nostoAnalytics = useNostoAnalytics();

  // Send tracking events when server data changes.
  useEffect(() => {
    if (200 !== serverData.status) return;

    tripleWhale.trackPageLoad("Flexible");
    nostoAnalytics.trackOtherPageView();
  }, [nostoAnalytics, tripleWhale, serverData]);

  // Page found, render flexible page.
  if (200 === serverData.status) {
    return <Sections page={serverData.page} />;
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  return null;
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;
  return (
    <MetaComponent
      meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
    />
  );
}
